import Component from '@glimmer/component';
import { service } from '@ember/service';
import Heading from 'tio-reimagined/components/heading';
import Drawer from 'tio-reimagined/components/drawer';
import { formatCurrency } from 'tio-reimagined/format';
import { t } from 'ember-intl';
import {
  PhCalendarDots,
  PhSealCheck,
  PhMoneyWavy,
  PhHourglassLow,
  PhCalendarBlank,
} from 'ember-phosphor-icons';
import type PlanModel from 'tio-data/models/plan';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from 'tio-employee/services/session-context';

/**
 * BenefitDetailDrawer — View Benefit plan details in a right drawer
 *
 * Displays plan name, plan description, contribution start date,
 * contribution amount with cadence, waiting period (if applicable),
 * annual limit (if applicable), and lifetime limit (if applicable).
 *
 * All values are pulled from the API-backed PlanModel and the
 * current Employee. Rows for waiting period, annual limit, and
 * lifetime limit are only rendered when the API provides a
 * positive value for that field.
 *
 * @example
 * ```gts
 * <BenefitDetailDrawer
 *   @isOpen={{this.showBenefitDrawer}}
 *   @onClose={{this.closeBenefitDrawer}}
 *   @plan={{this.plan}}
 * />
 * ```
 */
interface BenefitDetailDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** The employee's plan model */
    plan: PlanModel | null | undefined;
  };
  Element: HTMLDivElement;
}

export default class BenefitDetailDrawer extends Component<BenefitDetailDrawerSignature> {
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  get plan() {
    return this.args.plan;
  }

  get planName() {
    return this.plan?.name || this.intl.t('accounts.benefit_detail.default_plan_name');
  }

  get planDescription() {
    return this.plan?.description;
  }

  get hasWaitingPeriod() {
    const months = this.plan?.waitingPeriod;
    return months != null && months > 0;
  }

  get hasAnnualLimit() {
    const limit = this.plan?.annualLimit;
    return limit != null && limit > 0;
  }

  get hasLifetimeLimit() {
    const limit = this.plan?.lifetimeLimit;
    return limit != null && limit > 0;
  }

  get annualLimit() {
    const limit = this.plan?.annualLimit;
    if (limit == null) return '—';
    return formatCurrency(limit / 100);
  }

  get lifetimeLimit() {
    const limit = this.plan?.lifetimeLimit;
    if (limit == null) return '—';
    return formatCurrency(limit / 100);
  }

  // --- Contribution start date ---

  /**
   * Parses an API date string (typically `YYYY-MM-DD`) as a local
   * Date. Using `new Date('YYYY-MM-DD')` would be interpreted as
   * UTC midnight, which causes off-by-one display in negative
   * timezones — that was the source of the wrong start date.
   */
  private parseApiDate = (dateStr: string | undefined | null): Date | null => {
    if (!dateStr) return null;

    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (ymd) {
      const [, y, m, d] = ymd;
      const year = Number(y);
      const month = Number(m);
      const day = Number(d);
      const date = new Date(year, month - 1, day);

      if (isNaN(date.getTime())) return null;

      // Reject out-of-range values that JS would otherwise normalize
      // (e.g. 2024-13-40 → 2025-02-09).
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
      }

      return date;
    }

    // Fallback for non-standard date strings (e.g. ISO-8601 with a
    // time component such as "2024-01-15T12:00:00Z"). Anything the
    // YYYY-MM-DD branch above couldn't parse — including full ISO
    // timestamps from the API — comes through here. We accept JS's
    // default `new Date(...)` parsing for these and only reject the
    // result when it produces NaN.
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  /**
   * Resolves the contribution start date as a Date object based
   * on the plan's `milestoneDate`, sourcing the underlying date
   * from the appropriate API-backed Employee field. For
   * `_PLUS_WAIT` milestones, the plan's `waitingPeriod` and
   * `waitingPeriodUnit` are added to the base date.
   *
   * Returns null when no date can be sourced from the API
   * (e.g. `TODAY` or `FIRST_CONTRIBUTION` with no recorded
   * contribution yet) so the row can be hidden rather than
   * displaying a fabricated value.
   */
  get contributionStartDateValue() {
    const employee = this.sessionContext.currentEmployee;
    const milestone = this.plan?.milestoneDate?.toUpperCase();

    let baseDate: Date | null = null;

    switch (milestone) {
      case 'ELIGIBILITY_DATE':
      case 'ELIGIBILITY_DATE_PLUS_WAIT':
        baseDate = this.parseApiDate(employee?.eligibilityStartDate);
        break;
      case 'START_DATE':
      case 'START_DATE_PLUS_WAIT':
        baseDate = this.parseApiDate(employee?.employmentStartDate);
        break;
      case 'TODAY':
      case 'FIRST_CONTRIBUTION':
      default:
        // No reliable single date is exposed for these milestones
        // on the Employee/Plan models, so we don't fabricate one.
        return null;
    }

    if (!baseDate) return null;

    if (milestone?.endsWith('_PLUS_WAIT') && this.plan?.waitingPeriod) {
      const unit = this.plan.waitingPeriodUnit?.toLowerCase();
      if (unit === 'days') {
        baseDate.setDate(baseDate.getDate() + this.plan.waitingPeriod);
      } else {
        // Default to months (matches tio-payment behavior)
        baseDate.setMonth(baseDate.getMonth() + this.plan.waitingPeriod);
      }
    }

    return baseDate;
  }

  get contributionStartDate() {
    const d = this.contributionStartDateValue;
    if (!d) return '';

    return this.intl.formatDate(d, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  get cadenceUnit() {
    switch (this.plan?.cadence?.toUpperCase()) {
      case 'MONTHLY':
        return this.intl.t('accounts.benefit_detail.per_month');
      case 'QUARTERLY':
        return this.intl.t('accounts.benefit_detail.per_quarter');
      case 'SEMI_ANNUALLY':
        return this.intl.t('accounts.benefit_detail.per_semi_annual');
      case 'ANNUALLY':
        return this.intl.t('accounts.benefit_detail.per_year');
      default:
        return '';
    }
  }

  /**
   * Number of contribution periods per year for the plan's cadence.
   */
  get periodsPerYear() {
    switch (this.plan?.cadence?.toUpperCase()) {
      case 'MONTHLY':
        return 12;
      case 'QUARTERLY':
        return 4;
      case 'SEMI_ANNUALLY':
        return 2;
      case 'ANNUALLY':
        return 1;
      default:
        return 12;
    }
  }

  /**
   * Whether the tier duration is already captured by annual or lifetime limits.
   * If true, don't show duration on the contribution amount row.
   * If false, show estimated end year instead.
   */
  get isTierDurationCapturedByLimits() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return true;

    const hasDuration = tiers.some((tier) => tier.per > 0);
    if (!hasDuration) return true;

    // If tier total matches lifetime limit, the duration is captured
    const tierTotal = tiers.reduce((sum, tier) => sum + tier.amount * tier.per, 0);
    if (this.plan?.lifetimeLimit && tierTotal === this.plan.lifetimeLimit) {
      return true;
    }

    // If per-year amount matches annual limit, the duration is captured
    const firstTier = tiers[0];
    if (firstTier && this.plan?.annualLimit) {
      const annualFromTier = firstTier.amount * this.periodsPerYear;
      if (annualFromTier === this.plan.annualLimit) {
        return true;
      }
    }

    return false;
  }

  /**
   * Estimated end year from total tier periods,
   * anchored to the contribution start date. Returns null when no
   * start date can be resolved so callers can avoid showing an
   * "until" suffix anchored to today.
   */
  get estimatedEndYear() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return null;

    const totalPeriods = tiers.reduce((sum, tier) => sum + tier.per, 0);
    if (totalPeriods <= 0) return null;

    const startDate = this.contributionStartDateValue;
    if (!startDate) return null;

    const cadence = this.plan?.cadence?.toUpperCase();
    let monthsPerPeriod = 1;
    if (cadence === 'QUARTERLY') monthsPerPeriod = 3;
    if (cadence === 'SEMI_ANNUALLY') monthsPerPeriod = 6;
    if (cadence === 'ANNUALLY') monthsPerPeriod = 12;

    const totalMonths = totalPeriods * monthsPerPeriod;
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + totalMonths, 1);
    return endDate.getFullYear().toString();
  }

  /**
   * Formatted contribution amount including cadence, e.g. "$50/month".
   * Per spec:
   * - If tier duration IS captured by limits → just "$50/month"
   * - If tier duration is NOT captured by limits → "$50/month until ~2028"
   */
  get contributionWithCadence() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return '—';
    const amount = tiers[0]?.amount;
    if (!amount) return '—';
    const formattedAmount = formatCurrency(amount / 100);
    const unit = this.cadenceUnit;
    if (!unit) return formattedAmount;

    let label = `${formattedAmount}/${unit}`;

    // If tier duration is NOT captured by limits, show estimated end year
    if (!this.isTierDurationCapturedByLimits && this.estimatedEndYear) {
      label += ` ${this.intl.t('accounts.benefit_detail.until_year', { year: this.estimatedEndYear })}`;
    }

    return label;
  }

  get waitingPeriodLabel() {
    const months = this.plan?.waitingPeriod;
    if (months == null || months === 0) return '—';
    if (months === 1) return this.intl.t('accounts.benefit_detail.employed_one_month');
    return this.intl.t('accounts.benefit_detail.employed_months', { months });
  }

  <template>
    <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
      <:header>
        <div class="tio-col-tight">
          <Heading @level={{3}}>
            {{this.planName}}
          </Heading>
          {{#if this.planDescription}}
            <p class="font-medium leading-relaxed tio-fg-muted text-base">
              {{this.planDescription}}
            </p>
          {{/if}}
        </div>
      </:header>
      <:body>
        <div class="flex flex-col gap-8 px-8 py-10">
          {{! Contribution Start Date — only if resolvable from API data }}
          {{#if this.contributionStartDate}}
            <div class="tio-row-between">
              <div class="flex items-center gap-1.5">
                <PhCalendarBlank @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="font-semibold tio-fg-secondary text-base">{{t
                    "accounts.benefit_detail.contribution_start_date"
                  }}</span>
              </div>
              <span class="font-medium tio-fg-muted text-base">{{this.contributionStartDate}}</span>
            </div>
          {{/if}}

          {{! Contribution Amount with cadence (e.g. "$50/month") }}
          <div class="tio-row-between">
            <div class="flex items-center gap-1.5">
              <PhMoneyWavy @size={{20}} @weight="fill" class="tio-fg-secondary" />
              <span class="font-semibold tio-fg-secondary text-base">{{t
                  "accounts.benefit_detail.contribution_amount"
                }}</span>
            </div>
            <span class="font-medium tio-fg-muted text-base">{{this.contributionWithCadence}}</span>
          </div>

          {{! Waiting Period — only if applicable }}
          {{#if this.hasWaitingPeriod}}
            <div class="tio-row-between">
              <div class="flex items-center gap-1.5">
                <PhHourglassLow @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="font-semibold tio-fg-secondary text-base">{{t
                    "accounts.benefit_detail.waiting_period"
                  }}</span>
              </div>
              <span class="font-medium tio-fg-muted text-base">{{this.waitingPeriodLabel}}</span>
            </div>
          {{/if}}

          {{! Annual Limit — only if applicable }}
          {{#if this.hasAnnualLimit}}
            <div class="tio-row-between">
              <div class="flex items-center gap-1.5">
                <PhCalendarDots @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="font-semibold tio-fg-secondary text-base">{{t
                    "accounts.benefit_detail.annual_limit"
                  }}</span>
              </div>
              <span class="font-medium tio-fg-muted text-base">{{this.annualLimit}}</span>
            </div>
          {{/if}}

          {{! Lifetime Limit — only if applicable }}
          {{#if this.hasLifetimeLimit}}
            <div class="tio-row-between">
              <div class="flex items-center gap-1.5">
                <PhSealCheck @size={{20}} @weight="fill" class="tio-fg-secondary" />
                <span class="font-semibold tio-fg-secondary text-base">{{t
                    "accounts.benefit_detail.lifetime_limit"
                  }}</span>
              </div>
              <span class="font-medium tio-fg-muted text-base">{{this.lifetimeLimit}}</span>
            </div>
          {{/if}}

        </div>
      </:body>
    </Drawer>
  </template>
}
